import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { DOMAINS } from "@/lib/content";

export default function Expertise() {
    const location = useLocation();

    useEffect(() => {
        // Scroll to hash anchor after page mount
        if (location.hash) {
            const el = document.querySelector(location.hash);
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
            }
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [location]);

    return (
        <main data-testid="expertise-page" style={{ paddingTop: 140 }}>
            {/* Header */}
            <section style={{ padding: "40px 40px 80px" }}>
                <div className="mx-auto" style={{ maxWidth: 1400 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
                    >
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                            Domaines de prise en charge
                        </div>
                        <div className="divider-terra mt-3" />
                        <h1
                            className="font-display mt-8"
                            style={{
                                fontSize: "clamp(3rem, 7vw, 6.5rem)",
                                lineHeight: 0.95,
                                color: "var(--forest-dark)",
                                fontWeight: 400,
                                letterSpacing: "-0.02em",
                                maxWidth: 1000,
                            }}
                        >
                            Une clinique <span style={{ fontStyle: "italic", color: "var(--terra)" }}>précise</span>,
                            <br />trois compétences <span style={{ fontStyle: "italic" }}>reconnues.</span>
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Domain sections — alternating */}
            {DOMAINS.map((d, i) => {
                const isDark = i === 1;   // middle one in warm/dark contrast
                return (
                    <section
                        key={d.id}
                        id={d.id}
                        data-testid={`expertise-section-${d.id}`}
                        style={{
                            background: isDark ? "var(--forest-deep)" : "transparent",
                            color: isDark ? "var(--cream)" : "var(--ink)",
                            padding: "100px 40px",
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        {isDark && (
                            <>
                                <div className="breathe-slow" aria-hidden style={{ position: "absolute", right: -180, top: -180, width: 600, height: 600, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.14)" }} />
                                <div className="breathe" aria-hidden style={{ position: "absolute", left: -120, bottom: -120, width: 480, height: 480, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.09)" }} />
                            </>
                        )}

                        <div className="mx-auto grid grid-cols-1 md:grid-cols-12 gap-16 relative" style={{ maxWidth: 1400 }}>
                            {/* Left: giant number + picto */}
                            <div className="md:col-span-4">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.94 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
                                    style={{ position: "relative" }}
                                >
                                    <div
                                        className="font-display"
                                        style={{
                                            fontSize: "clamp(6rem, 15vw, 15rem)",
                                            lineHeight: 0.8,
                                            color: isDark ? "rgba(216, 155, 146, 0.6)" : "var(--terra)",
                                            fontStyle: "italic",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {d.num}
                                    </div>
                                    <div
                                        className="mt-6"
                                        style={{
                                            width: "100%",
                                            aspectRatio: "1 / 1",
                                            maxWidth: 320,
                                            background: isDark ? "rgba(255,255,255,0.06)" : "var(--cream-warm)",
                                            border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "var(--hairline)"}`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            position: "relative",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <img
                                            src={d.picto}
                                            alt=""
                                            style={{
                                                width: "58%",
                                                filter: isDark ? "brightness(0) invert(1) opacity(0.85)" : "brightness(0.4)",
                                                position: "relative",
                                                zIndex: 2,
                                            }}
                                        />
                                        <div className="breathe-slow" style={{ position: "absolute", inset: "16%", borderRadius: "50%", border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(107, 85, 72, 0.15)"}` }} />
                                        <div className="breathe" style={{ position: "absolute", inset: "26%", borderRadius: "50%", border: `1px solid ${isDark ? "rgba(216, 155, 146, 0.35)" : "rgba(216, 155, 146, 0.3)"}` }} />
                                    </div>
                                </motion.div>
                            </div>

                            {/* Right: content */}
                            <div className="md:col-span-8">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.9, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
                                >
                                    <div className="font-mono-clinical" style={{ color: isDark ? "var(--terra-light)" : "var(--forest)" }}>
                                        {d.subtitle}
                                    </div>
                                    <h2
                                        className="font-display mt-4"
                                        style={{
                                            fontSize: "clamp(2.6rem, 5.5vw, 5rem)",
                                            lineHeight: 0.95,
                                            fontWeight: 400,
                                            color: isDark ? "var(--cream)" : "var(--forest-dark)",
                                            letterSpacing: "-0.02em",
                                        }}
                                    >
                                        {d.title}
                                    </h2>
                                    <p
                                        className="font-display mt-6"
                                        style={{
                                            fontSize: "1.3rem",
                                            lineHeight: 1.6,
                                            color: isDark ? "var(--terra-light)" : "var(--ink-mid)",
                                            fontStyle: "italic",
                                            maxWidth: 640,
                                        }}
                                    >
                                        {d.lead}
                                    </p>

                                    <div className="mt-12">
                                        <div className="font-mono-clinical mb-4" style={{ color: isDark ? "var(--terra-light)" : "var(--terra)" }}>
                                            Indications principales
                                        </div>
                                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3" style={{ listStyle: "none", padding: 0 }}>
                                            {d.indications.map((ind) => (
                                                <li
                                                    key={ind}
                                                    className="flex items-start gap-3"
                                                    style={{
                                                        fontSize: 15,
                                                        color: isDark ? "rgba(251, 248, 243, 0.88)" : "var(--ink)",
                                                        lineHeight: 1.55,
                                                    }}
                                                >
                                                    <Check size={16} style={{ marginTop: 4, color: "var(--terra)", flexShrink: 0 }} />
                                                    {ind}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="mt-14 flex flex-wrap gap-4">
                                        <Link to="/rendez-vous" className={isDark ? "btn-primary" : "btn-primary"} data-testid={`expertise-cta-appointment-${d.id}`} style={isDark ? { background: "var(--terra)", borderColor: "var(--terra)" } : {}}>
                                            Adresser un patient <ArrowRight size={16} />
                                        </Link>
                                        <Link to="/contact" className="btn-ghost" style={isDark ? { color: "var(--cream)", borderColor: "rgba(255,255,255,0.3)" } : {}}>
                                            Discuter d'un cas
                                        </Link>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </section>
                );
            })}
        </main>
    );
}
